import { FC, ReactNode } from 'react';
import { cn } from '@new/lib';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';

export interface ProgressSegment {
  id: string;
  groupId: string | null;
  credits: number;
  color: string;
  title: string;
}

export interface ProgressLegendItem {
  label: string;
  color: string;
}

interface SegmentedProgressBarProps {
  segments: ProgressSegment[];
  total: number;
  legend?: ProgressLegendItem[];
  hint?: string;
  legendExtra?: ReactNode;
  onSelectGroup?: (groupId: string) => void;
  className?: string;
}

export const SegmentedProgressBar: FC<SegmentedProgressBarProps> = ({
  segments,
  total,
  legend,
  hint,
  legendExtra,
  onSelectGroup,
  className,
}) => {
  const segmentLabel = segments.map((segment) => segment.title).join('; ');

  const onSegmentClick = (groupId: string) => () => onSelectGroup?.(groupId);

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        role="group"
        aria-label={segmentLabel}
        className={cn(
          'flex h-5.25 w-full gap-0.5 rounded-xl! border border-(--neutral-black-NB300) p-0.75',
          className
        )}
      >
        {segments.map((segment, index) => {
          const clickable = onSelectGroup != null && segment.groupId != null;
          return (
            <Tooltip
              key={segment.id}
              content={segment.title}
              onClick={clickable ? onSegmentClick(segment.groupId ?? '') : undefined}
              style={{ width: `${(segment.credits / total) * 100}%` }}
              wrapperClassName={cn(
                'relative block h-full min-w-px appearance-none overflow-hidden border-0 p-0 transition-transform duration-150 cursor-pointer hover:z-20 hover:scale-y-125',
                segment.color,
                index === 0 && 'rounded-l-[10px]!',
                index === segments.length - 1 && 'rounded-r-[10px]!'
              )}
            >
              <span className="sr-only">{segment.title}</span>
            </Tooltip>
          );
        })}
      </div>

      {legend && legend.length > 0 && (
        <div className="flex items-center gap-5">
          {legend.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5">
              <span className={cn('size-1.5 rounded-sm!', item.color)} />
              <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                {item.label}
              </span>
            </span>
          ))}
          {hint && (
            <span className="ml-auto [font-size:var(--fs-l5)]! italic text-(--neutral-black-NB500)!">
              {hint}
            </span>
          )}
          {legendExtra && <span className="ml-auto">{legendExtra}</span>}
        </div>
      )}
    </div>
  );
};
